// components/common/CodeEditor.js
import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
} from "@mui/material";
import {
  ContentCopy as CopyIcon,
  Save as SaveIcon,
  Refresh as ResetIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import Editor from "@monaco-editor/react";

const SingleCodeEditor = ({
  title = "Code Editor",
  initialValue = "",
  language = "gherkin", // Default for feature files, can be javascript, typescript, etc.
  height = "400px",
  onSave = () => {},
  onContentChange = () => {},
  readOnly = false,
  showLineNumbers = true,
  theme = "vs-dark", // vs-dark, vs-light
  allowCopy = true,
  allowReset = true,
  allowEdit = true,
  customActions = [], // Array of custom action buttons
  autoSave = false,
  autoSaveDelay = 2000,
  showSaveNotification = true, // New prop to control save notifications
  sx = {},
}) => {
  const [content, setContent] = useState(initialValue);
  const [isEditing, setIsEditing] = useState(!readOnly);
  const [hasChanges, setHasChanges] = useState(false);
  const [showCopySuccess, setShowCopySuccess] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const editorRef = useRef(null);
  const timeoutRef = useRef(null);

  // Update content when initialValue changes
  useEffect(() => {
    if (initialValue !== content) {
      setContent(initialValue);
      setHasChanges(false);
    }
  }, [initialValue]);

  // Auto-save functionality
  useEffect(() => {
    if (autoSave && hasChanges && content !== initialValue) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        handleSave();
      }, autoSaveDelay);
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [content, hasChanges, autoSave, autoSaveDelay]);

  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor;

    // Custom black theme setup
    monaco.editor.defineTheme("customBlack", {
      base: "vs-dark",
      inherit: true,
      rules: [{ token: "", foreground: "ffffff", background: "000000" }],
      colors: {
        "editor.background": "#000000", // Pure black background
        "editor.foreground": "#ffffff", // White text
        "editorLineNumber.foreground": "#666666", // Gray line numbers
        "editor.selectionBackground": "#333333", // Dark gray selection
        "editor.lineHighlightBackground": "#111111", // Very dark gray for current line
        "editorCursor.foreground": "#ffffff", // White cursor
        "editor.selectionHighlightBackground": "#222222", // Dark selection highlight
        "editorIndentGuide.background": "#333333", // Dark indent guides
        "editorIndentGuide.activeBackground": "#666666", // Active indent guides
        "editorWidget.background": "#000000", // Widget background
        "dropdown.background": "#000000", // Dropdown background
        "list.background": "#000000", // List background
      },
    });

    // Apply the theme immediately
    monaco.editor.setTheme("customBlack");

    // Allow page scrolling when editor is not focused
    const editorElement = editor.getDomNode();
    if (editorElement) {
      editorElement.addEventListener(
        "wheel",
        (e) => {
          // Only prevent default if editor has focus or if actively editing
          if (!editor.hasTextFocus() && !isEditing) {
            e.stopPropagation();
            // Let the event bubble up to allow page scrolling
          }
        },
        { passive: true }
      );
    }

    // Set custom keybindings
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
      handleSave();
    });
  };

  const handleContentChange = (value) => {
    setContent(value || "");
    setHasChanges(value !== initialValue);
    onContentChange(value || "");
  };

  const handleSave = () => {
    onSave(content);
    setHasChanges(false);
    if (showSaveNotification) {
      setShowSaveSuccess(true);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setShowCopySuccess(true);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleReset = () => {
    setContent(initialValue);
    setHasChanges(false);
  };

  const toggleEditMode = () => {
    setIsEditing(!isEditing);
  };

  return (
    <Paper
      elevation={1}
      sx={{
        width: "100%",
        backgroundColor: theme === "vs-dark" ? "#000000" : "#ffffff", // Pure black
        border: "1px solid #333333", // Dark gray border
        borderRadius: "8px",
        overflow: "hidden",
        ...sx,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          backgroundColor: theme === "vs-dark" ? "#111111" : "#f5f5f5", // Very dark gray
          borderBottom: "1px solid #333333", // Dark gray border
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="h6"
            sx={{
              color: theme === "vs-dark" ? "#ffffff" : "#333333",
              fontFamily: "Manrope",
              fontSize: "16px",
              fontWeight: 600,
            }}
          >
            {title}
          </Typography>
          {hasChanges && (
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: "#ff9800",
                ml: 1,
              }}
            />
          )}
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          {/* Custom Actions */}
          {customActions.map((action, index) => (
            <Tooltip key={index} title={action.tooltip || action.label}>
              <IconButton
                onClick={action.onClick}
                size="small"
                sx={{ color: theme === "vs-dark" ? "#ffffff" : "#666666" }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}

          {/* Edit/View Toggle */}
          {allowEdit && (
            <Tooltip
              title={isEditing ? "Switch to View Mode" : "Switch to Edit Mode"}
            >
              <IconButton
                onClick={toggleEditMode}
                size="small"
                sx={{ color: theme === "vs-dark" ? "#ffffff" : "#666666" }}
              >
                {isEditing ? <ViewIcon /> : <EditIcon />}
              </IconButton>
            </Tooltip>
          )}

          {/* Copy Button */}
          {allowCopy && (
            <Tooltip title="Copy to clipboard">
              <IconButton
                onClick={handleCopy}
                size="small"
                sx={{ color: theme === "vs-dark" ? "#ffffff" : "#666666" }}
              >
                <CopyIcon />
              </IconButton>
            </Tooltip>
          )}

          {/* Reset Button */}
          {allowReset && hasChanges && (
            <Tooltip title="Reset to original">
              <IconButton
                onClick={handleReset}
                size="small"
                sx={{ color: theme === "vs-dark" ? "#ffffff" : "#666666" }}
              >
                <ResetIcon />
              </IconButton>
            </Tooltip>
          )}

          {/* Save Button */}
          {!autoSave && hasChanges && (
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              size="small"
              sx={{
                backgroundColor: "#0094FB",
                "&:hover": {
                  backgroundColor: "#0078D4",
                },
              }}
            >
              Save
            </Button>
          )}
        </Box>
      </Box>

      {/* Editor */}
      <Box sx={{ height }}>
        <Editor
          value={content}
          language={language}
          theme="customBlack" // Use the custom black theme
          onChange={handleContentChange}
          onMount={handleEditorDidMount}
          options={{
            readOnly: !isEditing,
            minimap: { enabled: false },
            lineNumbers: showLineNumbers ? "on" : "off",
            scrollBeyondLastLine: false,
            automaticLayout: true,
            fontSize: 14,
            fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
            wordWrap: "on",
            contextmenu: true,
            selectOnLineNumbers: true,
            roundedSelection: false,
            cursorStyle: "line",
            glyphMargin: false,
            folding: true,
            lineDecorationsWidth: 10,
            lineNumbersMinChars: 3,
            renderLineHighlight: "all",
            // Allow page scrolling when hovering over editor
            scrollbar: {
              alwaysConsumeMouseWheel: false,
            },
            // Disable editor scrolling when not focused
            mouseWheelScrollSensitivity: 0.5,
          }}
        />
      </Box>

      {/* Success Notifications */}
      <Snackbar
        open={showCopySuccess}
        autoHideDuration={2000}
        onClose={() => setShowCopySuccess(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setShowCopySuccess(false)}
          severity="success"
          variant="filled"
        >
          Content copied to clipboard!
        </Alert>
      </Snackbar>

      <Snackbar
        open={showSaveSuccess}
        autoHideDuration={2000}
        onClose={() => setShowSaveSuccess(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setShowSaveSuccess(false)}
          severity="success"
          variant="filled"
        >
          Content saved successfully!
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default SingleCodeEditor;
